/**
 * Dashboard FE API client — thin wrappers around axios for the
 * /api/v2/dashboard/* endpoints. Mirrors the existing legacy convention
 * (axios + process.env.REACT_APP_SERVER_URL + withCredentials handled
 * by the global axios defaults).
 *
 * All widget endpoints accept an optional `range` query param matching
 * the header dropdown — BE resolves the key into from/to dates and
 * filters the per-entity date column.
 */

import axios from "axios";
import type {
  AgingReport,
  ApiResponse,
  AppointmentsSummary,
  BootstrapPayload,
  EstimatesSummary,
  InvoicesSummary,
  InvoiceStatusFilter,
  LayoutItem,
  ProjectsList,
  RepairRequestsSummary,
  RequestsByType,
  SchedulesSummary,
  ServiceRequestsList,
  TimeOffUpcoming,
} from "./types";
import type { DashboardRangeKey } from "./RangeContext";

const BASE = `${process.env.REACT_APP_SERVER_URL}/api/v2/dashboard`;

async function unwrap<T>(res: { data: ApiResponse<T> }): Promise<T> {
  const payload = res.data;
  if (payload.success) {
    return payload.data;
  }
  const { error } = payload;
  const message =
    typeof error === "string"
      ? error
      : typeof error === "object" && error && "message" in error
        ? String((error as { message: unknown }).message)
        : "Unknown dashboard API error";
  throw new Error(message);
}

/** Build the shared { params: { range } } axios config when a key is given. */
function rangeParams(range?: DashboardRangeKey) {
  return range ? { params: { range } } : undefined;
}

export const fetchBootstrap = async (): Promise<BootstrapPayload> =>
  unwrap(await axios.get(`${BASE}/bootstrap`));

export const fetchLayout = async (): Promise<{ layout: LayoutItem[] }> =>
  unwrap(await axios.get(`${BASE}/layout`));

export const updateLayout = async (
  layout: LayoutItem[],
): Promise<{ layout: LayoutItem[] }> =>
  unwrap(await axios.put(`${BASE}/layout`, { layout }));

export const resetLayout = async (): Promise<{ layout: LayoutItem[] }> =>
  unwrap(await axios.delete(`${BASE}/layout`));

/* --- Widget data endpoints --- */

export const fetchAppointmentsSummary = async (
  range?: DashboardRangeKey,
): Promise<AppointmentsSummary> =>
  unwrap(await axios.get(`${BASE}/appointments-summary`, rangeParams(range)));

export const fetchInvoicesSummary = async (
  range?: DashboardRangeKey,
): Promise<InvoicesSummary> =>
  unwrap(await axios.get(`${BASE}/invoices-summary`, rangeParams(range)));

export const fetchEstimatesSummary = async (
  range?: DashboardRangeKey,
): Promise<EstimatesSummary> =>
  unwrap(await axios.get(`${BASE}/estimates-summary`, rangeParams(range)));

export const fetchRepairRequests = async (
  range?: DashboardRangeKey,
): Promise<RepairRequestsSummary> =>
  unwrap(await axios.get(`${BASE}/repair-requests`, rangeParams(range)));

export const fetchRequestsByType = async (
  range?: DashboardRangeKey,
): Promise<RequestsByType> =>
  unwrap(await axios.get(`${BASE}/requests-by-type`, rangeParams(range)));

export const fetchServiceRequestsList = async (): Promise<ServiceRequestsList> =>
  unwrap(await axios.get(`${BASE}/service-requests-list`));

export const fetchProjectsList = async (): Promise<ProjectsList> =>
  unwrap(await axios.get(`${BASE}/projects-list`));

export const fetchTimeOffUpcoming = async (
  range?: DashboardRangeKey,
): Promise<TimeOffUpcoming> =>
  unwrap(await axios.get(`${BASE}/time-off-upcoming`, rangeParams(range)));

export const fetchSchedulesSummary = async (days = 14): Promise<SchedulesSummary> =>
  unwrap(await axios.get(`${BASE}/schedules-summary`, { params: { days } }));

export const fetchAgingReport = async (
  status: InvoiceStatusFilter = "open",
  range?: DashboardRangeKey,
): Promise<AgingReport> =>
  unwrap(
    await axios.get(`${BASE}/aging-report`, {
      params: range ? { status, range } : { status },
    }),
  );
